import { useTranslation } from 'react-i18next';
import {
  Assignment as AssignmentIcon, 
  CheckCircle as CheckCircleIcon, 
  ListAlt as ListAltIcon, 
  QuestionAnswer as QuestionAnswerIcon,
  Person as PersonIcon,
  Description as DescriptionIcon,
  Assessment as AssessmentIcon,
  School as SchoolIcon,
  Business as BusinessIcon,
  Event as EventIcon,
  BarChart as BarChartIcon,
  Share as ShareIcon,
  Visibility as VisibilityIcon,
  Link as LinkIcon,
  Help as HelpIcon,
  VideoLibrary as VideoLibraryIcon,
  MenuBook as MenuBookIcon
} from '@mui/icons-material';

/**
 * Dashboard için yardımcı fonksiyonlar ve sabitler
 */

/**
 * Gün saatine göre karşılama mesajı
 */
export const getGreeting = (t) => {
  const hour = new Date().getHours();
  if (hour < 12) return t('good_morning');
  if (hour < 18) return t('good_day');
  return t('good_evening');
};

/**
 * İstatistik kartları konfigürasyonu
 */
export const getStatsConfig = (statsData, t) => [
  { 
    title: t('total_form'), 
    value: statsData.totalForms,
    icon: <AssignmentIcon />,
    color: '#4158D0',
    colorLight: '#C850C0',
    bg: 'rgba(65, 88, 208, 0.1)'
  },
  { 
    title: t('active_form'), 
    value: statsData.activeForms,
    icon: <ListAltIcon />,
    color: '#2ecc71',
    colorLight: '#27ae60',
    bg: 'rgba(46, 204, 113, 0.1)'
  },
  { 
    title: t('record_form'), 
    value: statsData.recordForms,
    icon: <CheckCircleIcon />,
    color: '#e74c3c',
    colorLight: '#c0392b',
    bg: 'rgba(231, 76, 60, 0.1)'
  },
  { 
    title: t('archive_form'), 
    value: statsData.archiveForms,
    icon: <QuestionAnswerIcon />,
    color: '#9b59b6',
    colorLight: '#8e44ad',
    bg: 'rgba(155, 89, 182, 0.1)'
  },
];

/**
 * Kategori listesi
 */
export const getCategories = (t) => [
  t('all'),
  t('survey'),
  t('application'),
  t('evaluation'),
  t('hr'),
  t('marketing'),
  t('education'),
  t('event')
];

/**
 * Template listesi
 */
export const getTemplates = (t) => [
  {
    id: 1,
    title: t('customer_satisfaction_survey'),
    description: t('customer_satisfaction_survey_desc'),
    category: t('survey'),
    icon: <AssessmentIcon />,
    color: '#4158D0',
    colorLight: '#C850C0',
    bg: 'rgba(65, 88, 208, 0.1)',
    usageCount: 1250
  },
  {
    id: 2,
    title: t('application_form'),
    description: t('application_form_desc'),
    category: t('application'),
    icon: <DescriptionIcon />,
    color: '#2ecc71',
    colorLight: '#27ae60',
    bg: 'rgba(46, 204, 113, 0.1)',
    usageCount: 980
  },
  {
    id: 3,
    title: t('employee_evaluation_form'),
    description: t('employee_evaluation_form_desc'),
    category: t('hr'),
    icon: <BusinessIcon />,
    color: '#e74c3c',
    colorLight: '#c0392b',
    bg: 'rgba(231, 76, 60, 0.1)',
    usageCount: 750
  },
  {
    id: 4,
    title: t('student_feedback_form'),
    description: t('student_feedback_form_desc'),
    category: t('education'),
    icon: <SchoolIcon />,
    color: '#9b59b6',
    colorLight: '#8e44ad',
    bg: 'rgba(155, 89, 182, 0.1)',
    usageCount: 620
  },
  {
    id: 5,
    title: t('event_registration_form'),
    description: t('event_registration_form_desc'),
    category: t('event'),
    icon: <EventIcon />,
    color: '#f39c12',
    colorLight: '#d35400',
    bg: 'rgba(243, 156, 18, 0.1)',
    usageCount: 450
  },
  {
    id: 6,
    title: t('market_research_survey'),
    description: t('market_research_survey_desc'),
    category: t('marketing'),
    icon: <BarChartIcon />,
    color: '#3498db',
    colorLight: '#2980b9',
    bg: 'rgba(52, 152, 219, 0.1)',
    usageCount: 380
  }
];

/**
 * Paylaşım istatistikleri
 */
export const getSharingStats = (t) => [
  {
    id: 1,
    title: t('customer_satisfaction_survey'),
    views: 1250,
    shares: 45,
    icon: <ShareIcon />,
    color: '#4158D0',
    bg: 'rgba(65, 88, 208, 0.1)'
  },
  {
    id: 2,
    title: t('application_form'),
    views: 980,
    shares: 32,
    icon: <VisibilityIcon />,
    color: '#2ecc71',
    bg: 'rgba(46, 204, 113, 0.1)'
  },
  {
    id: 3,
    title: t('employee_evaluation_form'),
    views: 750,
    shares: 28,
    icon: <LinkIcon />,
    color: '#e74c3c',
    bg: 'rgba(231, 76, 60, 0.1)'
  }
];

/**
 * İpuçları ve eğitimler
 */
export const getTips = (t) => [
  {
    id: 1,
    title: t('form_design_tips'),
    description: t('form_design_tips_desc'),
    icon: <HelpIcon />,
    color: '#4158D0',
    bg: 'rgba(65, 88, 208, 0.1)',
    link: '#'
  },
  {
    id: 2,
    title: t('video_tutorial'),
    description: t('video_tutorial_desc'),
    icon: <VideoLibraryIcon />,
    color: '#2ecc71',
    bg: 'rgba(46, 204, 113, 0.1)',
    link: '#'
  },
  {
    id: 3,
    title: t('form_analytics_guide'),
    description: t('form_analytics_guide_desc'),
    icon: <MenuBookIcon />,
    color: '#e74c3c',
    bg: 'rgba(231, 76, 60, 0.1)',
    link: '#'
  }
];

/**
 * Sayı formatla
 */
export const formatNumber = (number) => {
  return number?.toLocaleString() || '0';
};

/**
 * Avatar fallback ikonu
 */
export const getAvatarFallback = () => <PersonIcon />;
